import { db, workspaceAccount } from "./db.js";
import { guestRequest, GuestApiError, GuestFarm } from "./guestApi.js";
import { WorkLog, Project } from "../types.js";
export interface WorkState {
  projects: Project[];
  work: WorkLog[];
  can_review: boolean;
}
export const contributionRequest = <T>(
  farm: string,
  path = "",
  method = "GET",
  body?: unknown,
) =>
  guestRequest<T>(
    `/guest/contributions${path}?farm_id=${encodeURIComponent(farm)}`,
    method,
    body,
  );
const personalState=(state:WorkState):WorkState=>({...state,work:state.work.filter(w=>(w.contributor_email||w.user_id)===workspaceAccount)});
type Command = { farm: string; body: Record<string, unknown> };
export async function workState(requestedFarm?:string): Promise<WorkState> {
  const database = db;
  let farms = (await database.workspaceMeta.get("guest-farms"))?.value as
    GuestFarm[] | undefined;
  if (!farms?.length) farms = await guestRequest<GuestFarm[]>("/guest/farms");
  if (!farms?.length) throw new Error("Property access is required.");
  const selected=requestedFarm||(await database.workspaceMeta.get("active-farm"))?.value;
  const farm = farms.find(f=>f.farm_id===selected)?.farm_id||(!selected&&farms.length===1?farms[0].farm_id:null);
  if(!farm)throw new Error("Choose an accessible property.");
  const stateKey="contribution-state:"+farm;
  // Retain previously saved timer state during the scoped-cache upgrade.
  await database.transaction('rw',database.workspaceMeta,async()=>{
    const legacy=(await database.workspaceMeta.get('contribution-state'))?.value;
    if(legacy?.farm!==farm)return;
    if(!(await database.workspaceMeta.get(stateKey)))await database.workspaceMeta.put({key:stateKey,value:{...personalState(legacy),farm}});
    await database.workspaceMeta.delete('contribution-state');
  });
  await flushWork();
  if (db !== database) throw new Error("Account changed.");
  const pending =
    (await database.workspaceMeta.get("contribution-commands"))?.value || [];
  if (pending.some((c:Command)=>c.farm===farm)) {
    const saved = (await database.workspaceMeta.get(stateKey))
      ?.value;
    if (saved) {const own={...personalState(saved),farm};await database.workspaceMeta.put({key:stateKey,value:own});return own;}
  }
  try {
    const value = personalState(await contributionRequest<WorkState>(farm));
    if (db !== database) throw new Error("Account changed.");
    await database.workspaceMeta.put({
      key: stateKey,
      value: { ...value, farm },
    });
    return value;
  } catch (e) {
    if (db !== database) throw new Error("Account changed.");
    if (e instanceof GuestApiError) {
      if ([401, 403].includes(e.status))
        await database.workspaceMeta.delete(stateKey);
      throw e;
    }
    const saved = (await database.workspaceMeta.get(stateKey))
      ?.value;
    if (saved) return saved;
    throw e;
  }
}
let flushing: Promise<void> | null = null;
export async function flushWork() {
  if (!navigator.onLine) return;
  if (flushing) return flushing;
  const database = db;
  flushing = (async () => {
    let queue: Command[] =
      (await database.workspaceMeta.get("contribution-commands"))?.value || [];
    while (queue.length && database === db) {
      try {
        await contributionRequest(
          queue[0].farm,
          "/work",
          "POST",
          queue[0].body,
        );
      } catch (e) {
        if (e instanceof GuestApiError) {
          if ([401, 403].includes(e.status)) {
            await database.workspaceMeta.delete('contribution-state:'+queue[0].farm);
            const legacy=(await database.workspaceMeta.get('contribution-state'))?.value;
            if(legacy?.farm===queue[0].farm)await database.workspaceMeta.delete('contribution-state');
          }
          throw new GuestApiError(
            `${e.message} Your saved work needs reconciliation before it can be reviewed.`,
            e.status,
          );
        }
        return;
      }
      const sent=queue[0].body.operation_id;
      await database.transaction('rw',database.workspaceMeta,async()=>{
        const latest:Command[]=(await database.workspaceMeta.get('contribution-commands'))?.value||[];
        queue=latest.filter(c=>c.body.operation_id!==sent);
        await database.workspaceMeta.put({key:'contribution-commands',value:queue});
      });
    }
  })().finally(() => {
    flushing = null;
  });
  return flushing;
}
export async function timerCommand(
  command: string,
  id?: string,
  projectId?: string,
  farmId?: string,
): Promise<WorkLog> {
  const database=db,state=await workState(farmId);
  if(command==='start'&&state.work.some(w=>w.state!=='stopped'))throw new Error('Finish your current timer before starting another.');
  const farm=farmId||(await database.workspaceMeta.get('active-farm'))?.value||(await database.workspaceMeta.get('guest-farms'))?.value?.[0]?.farm_id;
  const stateKey='contribution-state:'+farm,saved=(await database.workspaceMeta.get(stateKey))?.value;
  const old = state.work.find((w) => w.id === id),
    at = new Date().toISOString();
  const intervals = structuredClone(old?.intervals || []);
  if (command === "start") intervals.push({ start: at, end: null });
  if (
    (command === "pause" || command === "stop") &&
    old?.state === "active" &&
    intervals.length
  )
    intervals[intervals.length - 1].end = at;
  if (command === "resume") intervals.push({ start: at, end: null });
  const next: WorkLog = {
    ...old,
    id: id || crypto.randomUUID(),
    project_id: projectId || old!.project_id,
    user_id: old?.user_id || workspaceAccount || "",
    contributor_email: old?.contributor_email || workspaceAccount || undefined,
    sync_pending: true,
    start_time: old?.start_time || at,
    intervals,
    revision: (old?.revision || 0) + 1,
    state:
      command === "pause"
        ? "paused"
        : command === "stop"
          ? "stopped"
          : "active",
    review_status: command === "stop" ? "pending" : "in_progress",
    end_time: command === "stop" ? at : undefined,
    server_version: 0,
    updated_at: at,
  };
  const body = {
    operation_id: crypto.randomUUID(),
    id: next.id,
    project_id: next.project_id,
    command,
    expected_revision: old?.revision || 0,
    at,
  };
  if (database !== db) throw new Error("Account changed.");
  await database.transaction('rw',database.workspaceMeta,async()=>{
  const queue:Command[]=(await database.workspaceMeta.get('contribution-commands'))?.value||[];
  await database.workspaceMeta.put({
    key: "contribution-commands",
    value: [...queue, { farm: saved.farm, body }],
  });
  await database.workspaceMeta.put({
    key: stateKey,
    value: {
      ...saved,
      work: [next, ...state.work.filter((w) => w.id !== next.id)],
    },
  });
  });
  window.dispatchEvent(new Event('project-work-changed'));
  await flushWork();
  if (navigator.onLine) {
    window.dispatchEvent(new Event("project-work-changed"));
    const fresh = await workState(farm);
    return fresh.work.find((w) => w.id === next.id) || next;
  }
  return next;
}

export async function discardWorkCommands() {
  const database = db;
  await database.workspaceMeta.delete("contribution-commands");
  await database.workspaceMeta.where('key').startsWith('contribution-state').delete();
}

export async function saveManualWork(farm:string,projectId:string,body:{operation_id:string;id:string;worked_on:string;manual_minutes:number;notes:string;expected_revision:number;reason?:string}):Promise<void>{
 const database=db,state=await workState(farm),key='contribution-state:'+farm;
 const old=state.work.find(w=>w.id===body.id);
 const command={...body,project_id:projectId,command:'manual'};
 await database.transaction('rw',database.workspaceMeta,async()=>{
  if(database!==db)throw new Error('Account changed.');
  const queue:Command[]=(await database.workspaceMeta.get('contribution-commands'))?.value||[];
  if(!queue.some(c=>c.body.operation_id===body.operation_id))queue.push({farm,body:command});
  const entry={...old,id:body.id,project_id:projectId,user_id:old?.user_id||workspaceAccount||'',contributor_email:old?.contributor_email||workspaceAccount||'',entry_kind:'manual',start_time:null,end_time:null,worked_on:body.worked_on,manual_minutes:body.manual_minutes,notes:body.notes,state:'stopped',revision:body.expected_revision+1,intervals:[],sync_pending:true,review_status:'pending',updated_at:new Date().toISOString(),server_version:0} as WorkLog;
  await database.workspaceMeta.put({key:'contribution-commands',value:queue});
  await database.workspaceMeta.put({key,value:{...state,farm,work:body.expected_revision>0&&!old?state.work:[entry,...state.work.filter(w=>w.id!==entry.id)]}});
 });
 window.dispatchEvent(new Event('project-work-changed'));
 await flushWork();
}
